import {
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  LookbackOption,
  MixpanelEvent,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  tabLabelerEnumerated,
  useMixpanel,
  usePersistedTabs,
  usePortal,
  useTabs,
  type PersistedDefaultTabProps,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import { useBlotterTableTabCloning } from '../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import { CustomerAssetTransactionsBlotterTable } from './CustomerAssetTransactionsBlotterTable';
import type { CustomerAssetTransactionsTabProps } from './types';

const CUSTOMER_ASSET_TRANSACTIONS_BLOTTER_ID = 'dealer/customer-margin/asset-transactions';
const DEFAULT_CUSTOMER_ASSET_TRANSACTIONS_TAB_ID = 'default-customer-asset-transactions';

const PRESET_TABS: PersistedDefaultTabProps<CustomerAssetTransactionsTabProps>[] = [
  {
    label: 'Asset Transactions',
    id: DEFAULT_CUSTOMER_ASSET_TRANSACTIONS_TAB_ID,
    closable: false,
    editable: false,
    defaultFilter: {
      _start: LookbackOption.Past24Hours,
    },
    defaultColumns: [],
  },
];

const NEW_TAB_DEFAULTS: CustomerAssetTransactionsTabProps = {
  defaultFilter: {
    _start: LookbackOption.Past24Hours,
  },
  defaultColumns: [],
};

const tabLabeler = tabLabelerEnumerated('Asset Transactions');

function getBlotterID(tabID: string) {
  return `${CUSTOMER_ASSET_TRANSACTIONS_BLOTTER_ID}/${tabID}`;
}

export function CustomerAssetTransactions() {
  const mixpanel = useMixpanel();
  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);

  const handleSelect = useCallback(() => setInitialFiltersOpen(false), []);
  const handleAdd = useCallback(() => {
    mixpanel.track(MixpanelEvent.AddTab);
    setInitialFiltersOpen(true);
  }, [mixpanel]);

  const persistedTabs = usePersistedTabs<CustomerAssetTransactionsTabProps>(CUSTOMER_ASSET_TRANSACTIONS_BLOTTER_ID, {
    defaultInitialItems: PRESET_TABS,
    defaultInitialSelectedIndex: 0,
    excludedIDs: PRESET_TABS.map(tab => tab.id || ''),
    onSelect: handleSelect,
  });

  const tabs = useTabs<CustomerAssetTransactionsTabProps>({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });

  const handleCloneTab = useCallback(() => {
    tabs.clone(tabs.selectedIndex);
  }, [tabs]);

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  return (
    <Tabs {...tabs} style={{ height: '100%' }} size={TabSize.Large}>
      <TabList flex="0 0 auto" isBordered rightItems={<Box ref={filtersContainerRef} />}>
        {tabs.items.map((tab, idx) => (
          <Tab key={idx} {...tab} reorderable={true} />
        ))}
      </TabList>
      <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
        {tabs.items.map((tab, idx) => {
          if (!tab.id) {
            return null;
          }

          return (
            <AccordionGroup key={tab.id}>
              <CustomerAssetTransactionsBlotterTable
                key={idx}
                blotterID={getBlotterID(tab.id)}
                tabLabel={tab.label}
                defaultColumns={'defaultColumns' in tab ? tab.defaultColumns : NEW_TAB_DEFAULTS.defaultColumns}
                defaultFilter={'defaultFilter' in tab ? tab.defaultFilter : NEW_TAB_DEFAULTS.defaultFilter}
                initialIsOpen={initialFiltersOpen}
                onCloneTab={handleCloneTab}
              />
            </AccordionGroup>
          );
        })}
      </TabPanels>
    </Tabs>
  );
}
